import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { StepGuide, useOnboarding, ModuleTips } from '../components/onboarding';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, Download, FolderOpen, File, Image, FileSpreadsheet, Edit2, Check, X, AlertCircle, Clock, CheckCircle2, RefreshCw, Eye, Loader2, Square, CheckSquare } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_CATEGORIES = [
  { id: 'receipts', name: 'Receipts', icon: FileText },
  { id: 'invoices', name: 'Invoices', icon: FileText },
  { id: 'tax_forms', name: 'Tax Forms', icon: FileSpreadsheet },
  { id: 'bank_statements', name: 'Bank Statements', icon: FileSpreadsheet },
  { id: 'contracts', name: 'Contracts', icon: File },
  { id: 'general', name: 'General', icon: FolderOpen }
];

const Documents = () => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const { triggerAction } = useOnboarding();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [uploadLimits, setUploadLimits] = useState({ maxDocumentSizeMB: 25 });
  const fileInputRef = useRef(null);
  
  // Edit document state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editFormData, setEditFormData] = useState({ filename: '', category: '' });
  const [saving, setSaving] = useState(false);
  
  // OCR Preview state
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
  const [ocrDocument, setOcrDocument] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  
  // Bulk selection state
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmDeleteDocId, setConfirmDeleteDocId] = useState(null);
  const [bulkDeleteDocArmed, setBulkDeleteDocArmed] = useState(false);
  
  // Failed uploads tracking (persistent)
  const [failedUploads, setFailedUploads] = useState([]);

  useEffect(() => {
    fetchDocuments();
    fetchUploadLimits();
  }, []);

  const fetchUploadLimits = async () => {
    try {
      const response = await axios.get(`${API}/upload-limits`);
      setUploadLimits(response.data);
    } catch (error) {
      // Upload limits unavailable
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error(t('toast.failed_to_load_documents'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadingFiles(files.map(f => ({ name: f.name, status: 'pending' })));
    
    const maxSize = uploadLimits.maxDocumentSizeMB * 1024 * 1024;
    let completed = 0;
    const newFailures = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Update current file status
      setUploadingFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'uploading' } : f
      ));
      
      // Check file size using limits from API
      if (file.size > maxSize) {
        const reason = `File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum: ${uploadLimits.maxDocumentSizeMB} MB`;
        toast.error(t('toast.upload_error', { name: file.name, reason }));
        setUploadingFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error', error: reason } : f
        ));
        newFailures.push({ name: file.name, error: reason, time: new Date().toLocaleTimeString() });
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', selectedCategory);

      try {
        await axios.post(`${API}/documents?category=${selectedCategory}`, formData, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        });
        
        setUploadingFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'complete' } : f
        ));
        completed++;
      } catch (error) {
        const reason = error.response?.data?.detail || error.response?.status === 413 ? 'File too large for server' : `Upload failed (${error.response?.status || 'network error'})`;
        setUploadingFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error', error: reason } : f
        ));
        newFailures.push({ name: file.name, error: reason, time: new Date().toLocaleTimeString() });
      }
      
      // Update progress
      setUploadProgress(Math.round(((i + 1) / files.length) * 100));
    }

    // Track failed uploads persistently
    if (newFailures.length > 0) {
      setFailedUploads(prev => [...newFailures, ...prev]);
    }

    // Show completion notification
    if (completed === files.length) {
      toast.success(t('toast.upload_success', { count: completed }));
      triggerAction('upload_document');
    } else if (completed > 0) {
      toast.warning(t('toast.upload_partial', { completed, total: files.length, failed: newFailures.length }));
      triggerAction('upload_document');
    } else {
      toast.error(t('toast.upload_all_failed', { count: files.length }));
    }

    setUploading(false);
    setUploadProgress(100);
    
    // Clear progress after delay
    setTimeout(() => {
      setUploadingFiles([]);
      setUploadProgress(0);
    }, 2000);
    
    fetchDocuments();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (documentId) => {
    if (confirmDeleteDocId !== documentId) {
      setConfirmDeleteDocId(documentId);
      toast.info('Click delete again to confirm', { duration: 3000 });
      setTimeout(() => setConfirmDeleteDocId(null), 3000);
      return;
    }
    setConfirmDeleteDocId(null);
    try {
      await axios.delete(`${API}/documents/${documentId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(t('toast.document_deleted'));
      fetchDocuments();
    } catch (error) {
      toast.error(t('toast.failed_to_delete_document'));
    }
  };

  const handleEditClick = (doc) => {
    setEditingDocument(doc);
    setEditFormData({
      filename: doc.filename,
      category: doc.category
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingDocument) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/documents/${editingDocument.document_id}`, editFormData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      
      toast.success(t('toast.document_updated_successfully'));
      setEditDialogOpen(false);
      setEditingDocument(null);
      fetchDocuments();
    } catch (error) {
      toast.error(t('toast.failed_to_update_document'));
    } finally {
      setSaving(false);
    }
  };

  const handleQuickCategoryChange = async (documentId, newCategory) => {
    try {
      await axios.put(`${API}/documents/${documentId}`, 
        { category: newCategory },
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      toast.success(t('toast.category_updated'));
      // Auto-trigger: Document categorized
      triggerAction('categorize_document');
      fetchDocuments();
    } catch (error) {
      toast.error(t('toast.failed_to_update_category'));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return Image;
    }
    if (['xlsx', 'xls', 'csv'].includes(ext)) {
      return FileSpreadsheet;
    }
    return FileText;
  };

  const getCategoryInfo = (catId) => {
    return DOCUMENT_CATEGORIES.find(c => c.id === catId) || { name: catId, icon: FolderOpen };
  };

  const getExtractionStatusBadge = (doc) => {
    const status = doc.extraction_status;
    if (!status) {
      return (
        <Badge className="bg-gray-100 text-gray-500 hover:bg-gray-100 text-xs">
          Not Extracted
        </Badge>
      );
    }
    
    switch (status) {
      case 'success':
        return (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Extracted ({doc.extraction_count || 0})
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-xs" title={doc.extraction_error}>
            <AlertCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      case 'no_data':
        return (
          <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100 text-xs" title="No transactions found">
            <AlertCircle className="w-3 h-3 mr-1" />
            No Data
          </Badge>
        );
      case 'processing':
        return (
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-xs">
            <Clock className="w-3 h-3 mr-1 animate-pulse" />
            Processing
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleResetExtraction = async (documentId) => {
    try {
      await axios.post(`${API}/documents/${documentId}/reset-extraction`, null, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(t('toast.extraction_status_reset_you_can_now_retry_importing_this_doc'));
      fetchDocuments();
    } catch (error) {
      toast.error(t('toast.failed_to_reset_extraction_status'));
    }
  };

  // Bulk selection handlers
  const toggleDocSelect = (docId) => {
    setSelectedDocs(prev => prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]);
  };

  const handleSelectAll = () => {
    if (selectedDocs.length === filteredDocuments.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(filteredDocuments.map(d => d.document_id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocs.length === 0) return;
    if (!bulkDeleteDocArmed) {
      setBulkDeleteDocArmed(true);
      toast.info(`Click again to delete ${selectedDocs.length} documents`, { duration: 3000 });
      setTimeout(() => setBulkDeleteDocArmed(false), 3000);
      return;
    }
    setBulkDeleteDocArmed(false);
    setBulkDeleting(true);
    let deleted = 0;
    for (const docId of selectedDocs) {
      try {
        await axios.delete(`${API}/documents/${docId}`, { headers: getAuthHeaders(), withCredentials: true });
        deleted++;
      } catch (error) {
        // continue
      }
    }
    toast.success(`Deleted ${deleted} document(s)`);
    setSelectedDocs([]);
    setBulkDeleting(false);
    fetchDocuments();
  };

  const dismissFailedUpload = (idx) => {
    setFailedUploads(prev => prev.filter((_, i) => i !== idx));
  };

  const handleOcrPreview = async (doc) => {
    setOcrDocument(doc);
    setOcrDialogOpen(true);
    setOcrLoading(true);
    setOcrResult(null);
    
    try {
      const response = await axios.post(`${API}/documents/${doc.document_id}/ocr-preview`, null, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setOcrResult(response.data);
    } catch (error) {
      toast.error(t('toast.failed_to_extract_text_from_document'));
      setOcrResult({ error: error.response?.data?.detail || 'OCR extraction failed' });
    } finally {
      setOcrLoading(false);
    }
  };

  const filteredDocuments = filterCategory === 'all' 
    ? documents 
    : documents.filter(d => d.category === filterCategory);

  const documentsByCategory = DOCUMENT_CATEGORIES.map(cat => ({
    ...cat,
    count: documents.filter(d => d.category === cat.id).length
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
  <>
    <DashboardLayout>
      <div className="space-y-6" data-testid="documents-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1">{t('documents.title')}</h1>
            <p className="text-muted-foreground">{t('documents.subtitle', 'Organize and store your tax documents')}</p>
          </div>
        </div>

        {/* Onboarding Step Guide */}
        <StepGuide stepId="documents" />

        {/* Upload Card */}
        <Card className="shadow-soft border-border/40" data-testid="upload-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-1">
                <h3 className="font-semibold mb-2">{t('documents.upload')}</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  {t('documents.upload_description', 'Upload receipts, invoices, tax forms, and other important documents.')}
                </p>
                <p className="text-xs text-blue-600 mb-4">
                  <strong>{t('documents.max_size')}:</strong> {uploadLimits.maxDocumentSizeMB} MB {t('documents.per_document', 'per document')}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="upload-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.webp,.txt"
                    data-testid="file-input"
                  />
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Choose Files'}
                  </Button>
                </div>
                
                {/* Tax Forms Helper Text */}
                {selectedCategory === 'tax_forms' && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800 font-medium mb-1">{t('documents.tax_forms_accepted', 'Accepted Tax Forms:')}</p>
                    <p className="text-xs text-amber-700">
                      {t('documents.tax_forms_list', '1099-NEC • 1099-K • 1099-MISC • 1099-INT • 1099-DIV • W-2 • W-9 • 1040 • Schedule C • K-1')}
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      {t('documents.tax_forms_tip', 'Tip: For 1099s you received, consider uploading them in Settings for automatic income tracking.')}
                    </p>
                  </div>
                )}
                
                {/* Upload Progress */}
                {uploading && (
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Uploading files...</span>
                      <span className="font-medium">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                    <div className="space-y-1 mt-2">
                      {uploadingFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          {file.status === 'pending' && <div className="w-3 h-3 rounded-full bg-gray-300" />}
                          {file.status === 'uploading' && <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />}
                          {file.status === 'complete' && <Check className="w-3 h-3 text-green-500" />}
                          {file.status === 'error' && <X className="w-3 h-3 text-red-500" />}
                          <span className={file.status === 'error' ? 'text-red-500' : ''}>{file.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="hidden md:flex w-32 h-32 rounded-lg bg-muted/50 border-2 border-dashed border-border items-center justify-center">
                <FolderOpen className="w-12 h-12 text-muted-foreground/50" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Failed Uploads Banner */}
        {failedUploads.length > 0 && (
          <Card className="border-red-200 bg-red-50 shadow-soft" data-testid="failed-uploads-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-red-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Failed Uploads ({failedUploads.length})
                </h4>
                <Button variant="ghost" size="sm" onClick={() => setFailedUploads([])} className="text-red-600 hover:text-red-800">
                  Clear All
                </Button>
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {failedUploads.map((fail, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm p-2 bg-white rounded border border-red-100">
                    <div className="flex items-center gap-2 min-w-0">
                      <X className="w-3 h-3 text-red-500 flex-shrink-0" />
                      <span className="font-medium truncate">{fail.name}</span>
                      <span className="text-red-600 text-xs truncate">— {fail.error}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-400">{fail.time}</span>
                      <button onClick={() => dismissFailedUpload(idx)} className="text-gray-400 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filterCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterCategory('all')}
            data-testid="filter-all"
          >
            All ({documents.length})
          </Button>
          {documentsByCategory.map(cat => (
            <Button
              key={cat.id}
              variant={filterCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterCategory(cat.id)}
              data-testid={`filter-${cat.id}`}
            >
              {cat.name} ({cat.count})
            </Button>
          ))}
        </div>

        {/* Documents List */}
        <Card className="shadow-soft border-border/40" data-testid="documents-list">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Your Documents
              </CardTitle>
              {filteredDocuments.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleSelectAll} data-testid="select-all-docs">
                    {selectedDocs.length === filteredDocuments.length && filteredDocuments.length > 0 ? (
                      <><CheckSquare className="w-4 h-4 mr-1" /> Deselect All</>
                    ) : (
                      <><Square className="w-4 h-4 mr-1" /> Select All</>
                    )}
                  </Button>
                  {selectedDocs.length > 0 && (
                    <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-btn">
                      <Trash2 className="w-4 h-4 mr-1" />
                      {bulkDeleting ? 'Deleting...' : `Delete ${selectedDocs.length} Selected`}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {filteredDocuments.length > 0 ? (
              <div className="space-y-3">
                {filteredDocuments.map((doc) => {
                  const FileIcon = getFileIcon(doc.filename);
                  const catInfo = getCategoryInfo(doc.category);
                  
                  return (
                    <div 
                      key={doc.document_id}
                      className={`flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg hover:bg-muted/50 transition-colors ${selectedDocs.includes(doc.document_id) ? 'bg-primary/5 ring-1 ring-primary/20' : 'bg-muted/30'}`}
                      data-testid={`document-${doc.document_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedDocs.includes(doc.document_id)}
                          onCheckedChange={() => toggleDocSelect(doc.document_id)}
                          className="mt-1 flex-shrink-0"
                        />
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <FileIcon className="w-5 h-5 text-primary" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm sm:text-base truncate">{doc.filename}</p>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm text-muted-foreground">
                            <Select
                              value={doc.category}
                              onValueChange={(value) => handleQuickCategoryChange(doc.document_id, value)}
                            >
                              <SelectTrigger className="h-6 w-auto px-2 text-xs border-none bg-muted hover:bg-muted/80">
                                <Badge variant="secondary" className="text-xs">
                                  {getCategoryInfo(doc.category).name}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {DOCUMENT_CATEGORIES.map(cat => (
                                  <SelectItem key={cat.id} value={cat.id}>
                                    {cat.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {getExtractionStatusBadge(doc)}
                            <span className="hidden sm:inline">•</span>
                            <span>{formatFileSize(doc.file_size)}</span>
                            <span className="hidden sm:inline">•</span>
                            <span>{formatDate(doc.uploaded_at)}</span>
                          </div>
                          {doc.extraction_status === 'failed' && doc.extraction_error && (
                            <p className="text-xs text-red-500 mt-1 truncate" title={doc.extraction_error}>
                              {doc.extraction_error}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 justify-end sm:justify-start pl-14 sm:pl-0">
                        {/* OCR Preview Button */}
                        {['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf'].includes(doc.filename.split('.').pop()?.toLowerCase()) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOcrPreview(doc)}
                            className="text-muted-foreground hover:text-purple-600"
                            title="Quick OCR Preview"
                            data-testid={`ocr-preview-${doc.document_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        {(doc.extraction_status === 'failed' || doc.extraction_status === 'no_data') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleResetExtraction(doc.document_id)}
                            className="text-muted-foreground hover:text-blue-600"
                            title="Reset extraction status to retry"
                            data-testid={`reset-extraction-${doc.document_id}`}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditClick(doc)}
                          className="text-muted-foreground hover:text-primary"
                          data-testid={`edit-doc-${doc.document_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleDelete(doc.document_id); }}
                          className={confirmDeleteDocId === doc.document_id ? "bg-red-500 text-white hover:bg-red-600" : "text-muted-foreground hover:text-destructive"}
                          data-testid={`delete-doc-${doc.document_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FolderOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No documents yet</p>
                <p className="text-sm">Upload your first document to get started</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Document Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="edit-document-dialog">
            <DialogHeader>
              <DialogTitle>Edit Document</DialogTitle>
              <DialogDescription>
                Change the document name or category
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-filename">Document Name</Label>
                <Input
                  id="edit-filename"
                  value={editFormData.filename}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, filename: e.target.value }))}
                  placeholder="Enter document name"
                  data-testid="edit-filename-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-category">Category</Label>
                <Select 
                  value={editFormData.category} 
                  onValueChange={(value) => setEditFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger data-testid="edit-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="cancel-edit-btn">
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* OCR Preview Dialog */}
        <Dialog open={ocrDialogOpen} onOpenChange={setOcrDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="ocr-preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-600" />
                Quick OCR Preview
              </DialogTitle>
              <DialogDescription>
                {ocrDocument?.filename} - AI-extracted text and data
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              {ocrLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600 mb-4" />
                  <p className="text-muted-foreground">Analyzing document with AI...</p>
                  <p className="text-sm text-muted-foreground mt-1">This may take a few seconds</p>
                </div>
              ) : ocrResult?.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Extraction Failed</span>
                  </div>
                  <p className="text-sm text-red-600 mt-2">{ocrResult.error}</p>
                </div>
              ) : ocrResult ? (
                <div className="space-y-4">
                  {/* Detected Transactions */}
                  {ocrResult.transactions && ocrResult.transactions.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Detected Transactions ({ocrResult.transactions.length})
                      </h4>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                        {ocrResult.transactions.slice(0, 10).map((txn, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm py-1 border-b border-green-100 last:border-0">
                            <div>
                              <span className="font-medium">{txn.description}</span>
                              <span className="text-green-600 ml-2 text-xs">{txn.category}</span>
                            </div>
                            <div className="text-right">
                              <span className="font-semibold">${txn.amount?.toFixed(2)}</span>
                              <span className="text-muted-foreground text-xs ml-2">{txn.date}</span>
                            </div>
                          </div>
                        ))}
                        {ocrResult.transactions.length > 10 && (
                          <p className="text-sm text-green-600 text-center pt-2">
                            +{ocrResult.transactions.length - 10} more transactions
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Extracted Text */}
                  {ocrResult.extracted_text && (
                    <div>
                      <h4 className="font-semibold mb-2">Extracted Text</h4>
                      <div className="bg-muted/50 rounded-lg p-3 max-h-60 overflow-y-auto">
                        <pre className="text-sm whitespace-pre-wrap font-mono text-muted-foreground">
                          {ocrResult.extracted_text}
                        </pre>
                      </div>
                    </div>
                  )}
                  
                  {/* Summary */}
                  {ocrResult.summary && (
                    <div>
                      <h4 className="font-semibold mb-2">AI Summary</h4>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <p className="text-sm text-purple-900">{ocrResult.summary}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* No data found */}
                  {!ocrResult.transactions?.length && !ocrResult.extracted_text && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                      <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                      <p className="font-medium text-yellow-800">No extractable data found</p>
                      <p className="text-sm text-yellow-600 mt-1">
                        This document may not contain readable text or transaction data.
                      </p>
                    </div>
                  )}
                </div>
              ) : null}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setOcrDialogOpen(false)}>
                  Close
                </Button>
                {ocrResult?.transactions?.length > 0 && (
                  <Button 
                    onClick={() => {
                      setOcrDialogOpen(false);
                      toast.info(t('toast.go_to_general_ledger_→_import_from_documents_to_import_these'));
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Import to Ledger
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
    <ModuleTips moduleId="documents" />
  </>
  );
};

export default Documents;
